// ────────────────────────────────────────────────────────────────────────────
// Norvig Stories — central source of truth for real success stories
// Used by: Landing (success-story-section + "more stories" row),
// /historier (index page), /historier/:id (detail page)
// ────────────────────────────────────────────────────────────────────────────
import { useLanguage } from '../contexts/LanguageContext';
import { STORIES_EN } from './stories_en';

export const STORIES = [
  {
    id: 'irris',
    name: 'Irris Hollanes Lua',
    role: 'Sykepleier',
    location: 'Helsefonna, Haugesund',
    sector: 'health',
    sectorLabel: 'Helse & omsorg',
    fromCountry: 'Filippinene',
    fromFlag: '🇵🇭',
    journey: 'Filippinene → Helsefonna',
    portrait:
      'https://customer-assets.emergentagent.com/job_197395ff-3e07-4847-9b1f-7d58a35184d1/artifacts/aw4ji36v_Irris_portrett_m%C3%B8rkt_bakgrunn.png',
    portraitLandscape:
      'https://customer-assets.emergentagent.com/job_197395ff-3e07-4847-9b1f-7d58a35184d1/artifacts/kmwex82t_Irris_portrett_1.jpeg',
    portraitPosition: '50% 30%',
    articleSubtitle: 'En reise fra besøksvisum til autorisert sykepleier — uten snarveier.',
    quote: 'Det er mulig å begynne på nytt. Det er mulig å finne sin plass.',
    pullQuote: 'Reisen fra Hasvik til Alta var lang. Men den lærte meg at jeg kunne. Steg for steg.',
    shortQuote:
      'Veien til Norge var ikke enkel. Men noen ganger finner man hjem der man minst forventer det. Norge er mitt hjem nå.',
    teaser:
      'Fra Filippinene til Norge — uten snarveier. I dag sykepleier i Haugesund.',
    stats: [
      { v: '15+', l: 'år i Norge' },
      { v: '3', l: 'kommuner' },
      { v: '2', l: 'autorisasjoner' },
    ],
    timeline: [
      { year: '2011', label: 'Ankomst', text: 'Kom til Norge på besøksvisum, uten norske ord.' },
      { year: '2020', label: 'Helsefagarbeider', text: 'Jobbet i Finnmark, lærte språket og bygde selvtillit.' },
      { year: '2022', label: 'Norskprøve', text: 'Reiste fra Hasvik til Alta for å ta prøven. Bestod.' },
      { year: '2023', label: 'Vindafjord', text: 'Flyttet sørover. Begynte autorisasjons­prosessen.' },
      { year: '2025', label: 'Sykepleier', text: 'Autorisert av Helsedirektoratet. Ansatt ved Helsefonna.' },
    ],
    body: [
      {
        text:
          'Irris Hollanes Lua kom til Norge fra Filippinene med ett visum og ingen norske ord. I dag jobber hun som sykepleier ved Helsefonna i Haugesund — etter å ha lært språket, fått utdanningen godkjent og bygd seg opp fra helsefagarbeider i Finnmark.',
        dropCap: true,
      },
      {
        text:
          'Veien gikk gjennom lange reiser, krevende prøver og søvnløse netter. Hun husker spesielt reisen fra Hasvik til Alta for å ta norskprøven. **Men hun ga ikke opp.** Steg for steg bygget hun seg opp — fra Finnmark, til Vindafjord, til Haugesund. I dag er hun sykepleier med erfaring fra både medisinske og kirurgiske avdelinger, og leder KALAYAAN Norge Haugaland og er medlem av Utvalg for flerkulturell medvirkning i Rogaland.',
      },
    ],
    bullets: [
      'Fra besøksvisum til autorisert sykepleier — uten snarveier',
      'Norskprøver, godkjenning fra Helsedirektoratet, fagopplæring',
      'I dag aktiv i flerkulturelt arbeid og integrering',
    ],
  },
  {
    id: 'alexandru',
    name: 'Alexandru',
    role: 'Sykepleier',
    location: 'Kommunehelsetjeneste',
    sector: 'health',
    sectorLabel: 'Helse & omsorg',
    fromCountry: 'Romania',
    fromFlag: '🇷🇴',
    journey: 'Reisesykepleier → Fast ansatt',
    portrait:
      'https://customer-assets.emergentagent.com/job_197395ff-3e07-4847-9b1f-7d58a35184d1/artifacts/4s82xnr3_Alexandru_portrett_1.png',
    portraitPosition: '50% 22%',
    articleSubtitle: 'Fra fleksibilitet til fast ansettelse — han fant det beste fra to verdener.',
    quote: 'Å jobbe som reisesykepleier er som et intensivkurs i mestring.',
    pullQuote: 'Du må være trygg på deg selv. Men det gir deg en unik evne til å se løsninger der andre ser hindringer.',
    shortQuote:
      'Det krever at du er trygg på deg selv, men det gir deg en unik evne til å se løsninger der andre ser hindringer.',
    teaser:
      'Fra Romania til Nord-Norge til Sør-Norge — reisesykepleier som fant fast jobb i kommunen.',
    stats: [
      { v: '4+', l: 'år i Norge' },
      { v: '8+', l: 'kommuner' },
      { v: '3', l: 'spesialfelt' },
    ],
    timeline: [
      { year: '2021', label: 'Ankomst', text: 'Kom fra Romania som autorisert sykepleier.' },
      { year: '2022', label: 'Vikar', text: 'Begynte som reisesykepleier i Nord-Norge.' },
      { year: '2023', label: 'Spesialisering', text: 'Erfaring fra epilepsi-behandling og autisme-arbeid.' },
      { year: '2024', label: 'Familieliv', text: 'Familien vokste — søkte stabilitet.' },
      { year: '2025', label: 'Fast ansatt', text: 'Valgte fast jobb i kommunehelsetjenesten.' },
    ],
    body: [
      {
        text:
          'Alexandru kom fra Romania i 2021, og har siden brukt landet som sin læringsarena. Som vikar og reisesykepleier har han jobbet på tvers av kommunegrenser — fra de værbitte kystene i Nord-Norge til de dype dalene i Sør-Norge.',
        dropCap: true,
      },
      {
        text:
          'Vikar-tilværelsen ga ham muligheten til å oppleve alt fra spesialisert epilepsi-behandling og arbeid med autisme, til den nære hverdagen i hjemmetjenesten. **Men da familien vokste, søkte han fast ansettelse.** I 2025 valgte han stabilitet og forutsigbarhet — og bygger nå dype relasjoner i lokalsamfunnet, samtidig som han kommer hjem til familien hver dag.',
      },
    ],
    bullets: [
      'Reisesykepleier i Nord- og Sør-Norge — bred faglig erfaring',
      'Spesialisert erfaring: epilepsi, autisme, hjemmetjeneste',
      'Fra fleksibilitet til fast ansettelse — det beste fra to verdener',
    ],
  },
  {
    id: 'jurgen',
    name: 'Jurgen',
    role: 'Sesongarbeider',
    location: 'Hardanger',
    sector: 'sesong',
    sectorLabel: 'Sesongarbeid',
    fromCountry: 'Albania',
    fromFlag: '🇦🇱',
    journey: 'Sesongarbeid → Karriere i økonomi',
    portrait:
      'https://customer-assets.emergentagent.com/job_197395ff-3e07-4847-9b1f-7d58a35184d1/artifacts/fhzv0fhm_Jurgen.png',
    portraitPosition: '50% 22%',
    articleSubtitle: 'Sesongarbeid var inngangsdøren. Nå bygger han karrieren steg for steg.',
    quote: 'Han er ikke i mål ennå, men han er på riktig vei.',
    pullQuote: 'Sesongarbeid var min vei inn til Norge. Nå bygger jeg karrieren — steg for steg.',
    shortQuote:
      'Sesongarbeid var min vei inn til Norge. Nå bygger jeg karrieren steg for steg.',
    teaser:
      'Fra sesongarbeid i Hardanger mot drømmejobben innen økonomi — utdanning godkjent, språket på vei.',
    stats: [
      { v: '3+', l: 'sesonger' },
      { v: '1', l: 'godkjent utdanning' },
      { v: 'A1', l: 'norsknivå' },
    ],
    timeline: [
      { year: '2022', label: 'Første sesong', text: 'Kom fra Albania til Hardanger.' },
      { year: '2023', label: 'Pålitelig hånd', text: 'Andre sesong — vant tillit hos arbeidsgiveren.' },
      { year: '2024', label: 'Norsk', text: 'Lærte språket målrettet mellom sesongene.' },
      { year: '2025', label: 'Godkjenning', text: 'Utdanning innen økonomi godkjent i Norge.' },
      { year: '2026', label: 'Mot mål', text: 'Bygger nettverk og søker karriere i økonomi.' },
    ],
    body: [
      {
        text:
          'Jurgen kom til Norge fra Albania som sesongarbeider — med en solid utdanning innen økonomi og et klart mål: å bruke kompetansen sin og bygge en karriere her. På gården i Hardanger, der de ofte kaller ham Jørgen, viste han seg gjennom flere sesonger som pålitelig, arbeidsom og tilpasningsdyktig.',
        dropCap: true,
      },
      {
        text:
          'Mellom sesongene jobbet han målrettet for å lære språket, bygge nettverk og skaffe seg referanser fra norske arbeidsgivere. **I dag er utdanningen hans innen økonomi godkjent i Norge** — et viktig steg, men bare en del av reisen. Han er ikke i mål ennå, men på riktig vei. Og det er nettopp det som skaper en god og trygg fremtid.',
      },
    ],
    bullets: [
      'Sesongarbeid i Hardanger gjennom flere år — bygde norsk arbeidserfaring',
      'Utdanning innen økonomi og finans godkjent i Norge',
      'Lærer språket, bygger nettverk og referanser steg for steg',
    ],
  },
  {
    id: 'kelsey',
    name: 'Kelsey Wilson',
    role: 'Fysioterapiassistent',
    location: 'Eldreomsorg & rehabilitering',
    sector: 'health',
    sectorLabel: 'Helse & omsorg',
    fromCountry: 'USA',
    fromFlag: '🇺🇸',
    journey: 'USA → Norsk eldreomsorg',
    portrait:
      'https://customer-assets.emergentagent.com/job_197395ff-3e07-4847-9b1f-7d58a35184d1/artifacts/pr0r5hha_Kelsey_portrett.jpg',
    portraitPosition: '50% 28%',
    articleSubtitle: 'Hun har bygd livet i Norge. Nå venter hun bare på sjansen til å bidra.',
    quote: 'Jeg har funnet mitt hjem i Norge. Nå venter jeg bare på muligheten til å bidra.',
    pullQuote: 'Reisen er ikke over, men retningen er klar — jeg vil bidra i norsk eldreomsorg.',
    shortQuote:
      'Med høy kompetanse og dypt ønske om å bidra — reisen er ikke over, men retningen er klar.',
    teaser:
      'Fra Nord-California til norsk eldreomsorg — fysioterapeut som lærer språket og venter på godkjenning.',
    stats: [
      { v: '41', l: 'år' },
      { v: '2', l: 'utdanninger' },
      { v: '2026', l: 'flyttet' },
    ],
    timeline: [
      { year: '2020', label: 'A.A.S.-grad', text: 'Fysioterapiassistent fra Mt. Hood Community College.' },
      { year: '2024', label: 'Møtte forloveden', text: 'Begynte å planlegge livet i Norge.' },
      { year: '2026', label: 'Flyttet', text: 'Kom til Norge for å bygge livet sammen.' },
      { year: 'Nå', label: 'Norskkurs', text: 'Lærer språket på vei mot godkjenning.' },
    ],
    body: [
      {
        text:
          'Kelsey Wilson er en 41 år gammel fysioterapiassistent fra Nord-California, med bachelor i teaterkunst fra California State University, Chico, og en A.A.S.-grad fra Mt. Hood Community College i Oregon. Hun flyttet til Norge i 2026 for å bygge et liv sammen med sin forlovede — og for å bidra til norsk eldreomsorg og rehabilitering.',
        dropCap: true,
      },
      {
        text:
          'I dag jobber hun målrettet med å styrke norsken og kvalifisere seg for arbeid i helsesektoren, samtidig som hun navigerer et komplekst godkjennings-system. **Reisen er ikke over, men retningen er klar.** Kelsey representerer mange internasjonale fagarbeidere som ønsker å bidra i Norge — med høy kompetanse, sterk motivasjon og et dypt ønske om å bli en del av det norske samfunnet.',
      },
    ],
    bullets: [
      'Bachelor i teaterkunst + A.A.S. som fysioterapiassistent (USA)',
      'Mål: arbeid med eldre i hjemmetjenester og omsorgsboliger',
      'Lærer norsk og venter på godkjenning — fast bestemt på å bidra',
    ],
  },
  {
    id: 'alfons',
    name: 'Alfons',
    role: 'Industriarbeider',
    location: 'Boliden Odda',
    sector: 'service',
    sectorLabel: 'Service & transport',
    fromCountry: 'Albania',
    fromFlag: '🇦🇱',
    journey: 'Sesongarbeid → Industri',
    portrait:
      'https://customer-assets.emergentagent.com/job_homepage-redesign-39/artifacts/jjc6qcd7_IMG-20260418-WA0000.jpg',
    portraitPosition: '50% 30%',
    articleSubtitle:
      'Fra sesongarbeider på Måge gård til verdensledende industri ved Boliden Odda.',
    quote: 'Historien handler ikke om flaks. Den handler om muligheter.',
    pullQuote:
      'Han møtte opp hver dag med riktig innstilling, lærte raskt, tok ansvar — og bygget tillit steg for steg.',
    shortQuote:
      'Historien om Alfons handler ikke om flaks. Den handler om muligheter — og om å gripe dem.',
    teaser:
      'Fra Måge gård i Hardanger til Boliden Odda — sesongarbeider som ble industri-ressurs.',
    stats: [
      { v: '7+', l: 'år i Norge' },
      { v: '3', l: 'sektorer' },
      { v: '1', l: 'fast jobb' },
    ],
    timeline: [
      { year: '2019', label: 'Ankomst', text: 'Kom til Norge med en koffert og et åpent sinn.' },
      { year: '2022', label: 'Måge gård', text: 'Sesongarbeider i Hardanger. Lange dager, ukjent språk.' },
      { year: '2023', label: 'Tillit', text: 'Bygde tillit gjennom flere sesonger og tydelig vilje.' },
      { year: '2024', label: 'Bygg & anlegg', text: 'Første steg ut av sesongarbeid — ny bransje, nye relasjoner.' },
      { year: '2026', label: 'Boliden Odda', text: 'Fast jobb i en av verdens ledende industribedrifter.' },
    ],
    body: [
      {
        text:
          'Alfons kom til Norge med en koffert, et åpent sinn og en tydelig målsetting: å skape seg en bedre fremtid. Han startet som sesongarbeider på en gård i Hardanger. Arbeidet var både givende og krevende, dagene lange og språket ukjent. Men Alfons hadde noe som ikke kan læres — vilje. Han møtte opp hver dag med riktig innstilling, lærte raskt, tok ansvar og bygget tillit steg for steg.',
        dropCap: true,
      },
      {
        text:
          'Gjennom flere sesonger utviklet han ikke bare ferdigheter innen arbeid, men også forståelse for norsk arbeidskultur: punktlighet, samarbeid, ærlighet og kvalitet i alle ledd. Han tok initiativ til å lære språket, knyttet relasjoner til kollegaer og arbeidsgivere, og skaffet seg sterke referanser. **Det var her reisen virkelig begynte.**',
      },
      {
        text:
          'Gjennom nettverket han bygget, åpnet nye muligheter seg. Med erfaring, dokumentert arbeidsmoral og gode anbefalinger, fikk Alfons etter hvert sjansen i en helt annen bransje — først innen bygg og anlegg, deretter i en av verdens ledende industribedrifter, Boliden Odda.',
      },
      {
        text:
          'I dag er han ikke bare en ansatt — han er en ressurs. En person som bedriften stoler på. En som bidrar til verdiskaping, stabilitet og kvalitet. **Historien om Alfons handler ikke om flaks. Den handler om muligheter — og om å gripe dem.**',
      },
    ],
    bullets: [
      'Fra sesongarbeider på Måge gård i Hardanger',
      'Bygde norsk arbeidserfaring og språk steg for steg',
      'Fast ansatt ved Boliden Odda — verdensledende industri',
    ],
  },
  {
    id: 'dila',
    name: 'Dila',
    role: 'Barnehagelærer',
    location: 'Aga barnehage',
    sector: 'oppvekst',
    sectorLabel: 'Oppvekst',
    fromCountry: 'Albania',
    fromFlag: '🇦🇱',
    journey: 'Sesongarbeid → Barnehagelærer',
    portrait:
      'https://customer-assets.emergentagent.com/job_homepage-redesign-39/artifacts/za0pzsgf_file_00000000026c7243aa96bfcfb3dbe125.png',
    portraitPosition: '50% 20%',
    articleSubtitle:
      'Fra sesongarbeider i Hardanger til fast jobb som barnehagelærer ved Aga barnehage.',
    quote: 'Hun er ikke lenger på vei — hun har kommet frem.',
    pullQuote:
      'Dagene startet tidlig og var lange ute på garden. Men etter arbeidstid begynte hennes egentlige reise.',
    shortQuote: 'Hun er ikke lenger på vei — hun har kommet frem.',
    teaser:
      'Fra Hardanger til Aga barnehage — barnehagelærer-utdanningen godkjent etter målrettet innsats.',
    stats: [
      { v: '7+', l: 'år i Norge' },
      { v: '1', l: 'godkjent utdanning' },
      { v: '1', l: 'fast jobb' },
    ],
    timeline: [
      { year: '2019', label: 'Hardanger', text: 'Kom til Norge som sesongarbeider. Alt var nytt.' },
      { year: '2023', label: 'Norsk', text: 'Lærte språket ord for ord, etter arbeidstid.' },
      { year: '2024', label: 'Godkjenning', text: 'Begynte arbeidet med å få utdanningen godkjent.' },
      { year: '2025', label: 'Aga barnehage', text: 'Fast ansatt som barnehagelærer.' },
      { year: '2026', label: 'Hjemme', text: 'Hun har kommet frem.' },
    ],
    body: [
      {
        text:
          'Da Dila kom til Hardanger som sesongarbeider, var alt nytt. Språket, menneskene, hverdagen. Hun var langt hjemmefra, men bar med seg en stille styrke og en tydelig drøm: å skape et liv i Norge, og jobbe med det hun brant for.',
        dropCap: true,
      },
      {
        text:
          'Dagene startet tidlig og var lange ute på garden. Men etter arbeidstid begynte hennes egentlige reise. Hun øvde på norsk, ord for ord. Hun lærte gjennom samtaler, gjennom feil, gjennom mot. **Hun ga ikke opp, selv når det var vanskelig.**',
      },
      {
        text:
          'Dila hadde utdanning som barnehagelærer fra før, men veien til å få den godkjent i Norge var lang. Likevel tok hun ett steg av gangen. Hun navigerte i systemer, leverte dokumentasjon, ventet — og fortsatte å tro på målet sitt. Det var ikke én stor mulighet som endret alt. Det var mange små valg. Mange dager med innsats ingen så. Og så, en dag, var hun der.',
      },
      {
        text:
          'I dag jobber Dila fast ved Aga barnehage. Hun møter barn med trygghet og varme, snakker språket hun en gang strevde med, og er en viktig del av et fellesskap. **Hun er ikke lenger på vei — hun har kommet frem.**',
      },
    ],
    bullets: [
      'Fra sesongarbeider i Hardanger til barnehagelærer i Norge',
      'Utdanning som barnehagelærer godkjent — steg for steg',
      'Fast ansatt ved Aga barnehage, Hardanger',
    ],
  },
];

export const getStoryById = (id) => STORIES.find((s) => s.id === id);
export const getFeaturedStory = () => STORIES[0];
export const getOtherStories = () => STORIES.slice(1);
export const getRelatedStories = (id, limit = 3) =>
  STORIES.filter((s) => s.id !== id).slice(0, limit);

// ────────────────────────────────────────────────────────────────────────────
// i18n-aware story helpers. They overlay STORIES_EN strings on top of the
// base NO entries (images, ids, sector keys remain identical).
// ────────────────────────────────────────────────────────────────────────────
const overlayStory = (base, en) => (en ? { ...base, ...en } : base);

const buildLocalised = (lang) => {
  if (lang !== 'en') return STORIES;
  return STORIES.map((s) => overlayStory(s, STORIES_EN[s.id]));
};

export const useStories = () => {
  const { language } = useLanguage();
  return buildLocalised(language);
};

export const useStoryById = (id) => {
  const list = useStories();
  return list.find((s) => s.id === id) || null;
};

export const useFeaturedStory = () => useStories()[0];

export const useOtherStories = () => useStories().slice(1);

export const useRelatedStories = (id, limit = 3) =>
  useStories().filter((s) => s.id !== id).slice(0, limit);

